import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { TextField, TextArea, CheckField, JsonField } from './CrudTable';

export default function AdminSettings() {
  const [s, setS] = useState({ site_name: '', tagline: '', email: '', phone: '', phone_secondary: '', address: '', map_query: '', social: {}, offer_enabled: true, offer_badge: '', offer_title: '', offer_subtitle: '', offer_price: '', offer_original_price: '', offer_features: [], offer_cta_label: '', offer_cta_url: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.from('site_settings').select('*').eq('id', 1).maybeSingle().then(({ data }) => {
      if (data) setS(prev => ({ ...prev, ...data, social: data.social || {}, offer_features: data.offer_features || [] }));
    });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.from('site_settings').upsert({ ...s, id: 1, updated_at: new Date().toISOString() });
    setLoading(false);
    if (error) toast.error(error.message); else toast.success('Settings saved');
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-3xl text-ink-900">Website settings</h1>
      <p className="mt-1 text-sm text-ink-500">Identity, contact information, socials and the flagship offer.</p>
      <form onSubmit={save} className="mt-8 space-y-6">
        <div className="space-y-4 rounded-3xl border border-ink-100 bg-white p-8">
          <h2 className="font-display text-lg text-ink-900">Identity</h2>
          <TextField label="Site name" value={s.site_name} onChange={v => setS({ ...s, site_name: v })} />
          <TextField label="Tagline" value={s.tagline} onChange={v => setS({ ...s, tagline: v })} />
        </div>

        <div className="space-y-4 rounded-3xl border border-ink-100 bg-white p-8">
          <h2 className="font-display text-lg text-ink-900">Contact</h2>
          <TextField label="Email" value={s.email} onChange={v => setS({ ...s, email: v })} />
          <TextField label="Phone (primary)" value={s.phone} onChange={v => setS({ ...s, phone: v })} />
          <TextField label="Phone (secondary, e.g. US)" value={s.phone_secondary} onChange={v => setS({ ...s, phone_secondary: v })} />
          <TextArea label="Office address" value={s.address} onChange={v => setS({ ...s, address: v })} />
          <TextField label="Map search query (used by Google Maps embed)" value={s.map_query} onChange={v => setS({ ...s, map_query: v })} />
        </div>

        <div className="space-y-4 rounded-3xl border border-ink-100 bg-white p-8">
          <h2 className="font-display text-lg text-ink-900">Social</h2>
          <TextField label="LinkedIn URL" value={s.social.linkedin} onChange={v => setS({ ...s, social: { ...s.social, linkedin: v } })} />
          <TextField label="Instagram URL" value={s.social.instagram} onChange={v => setS({ ...s, social: { ...s.social, instagram: v } })} />
          <TextField label="Facebook URL" value={s.social.facebook} onChange={v => setS({ ...s, social: { ...s.social, facebook: v } })} />
        </div>

        <div className="space-y-4 rounded-3xl border border-cyan/30 bg-cyan-soft/40 p-8">
          <h2 className="font-display text-lg text-ink-900">Flagship offer (shown at top of /pricing)</h2>
          <CheckField label="Enable offer" value={s.offer_enabled} onChange={v => setS({ ...s, offer_enabled: v })} />
          <TextField label="Badge (small caps)" value={s.offer_badge} onChange={v => setS({ ...s, offer_badge: v })} />
          <TextField label="Title" value={s.offer_title} onChange={v => setS({ ...s, offer_title: v })} />
          <TextArea label="Subtitle" value={s.offer_subtitle} onChange={v => setS({ ...s, offer_subtitle: v })} />
          <div className="grid grid-cols-2 gap-4">
            <TextField label="Price (e.g. $3,900)" value={s.offer_price} onChange={v => setS({ ...s, offer_price: v })} />
            <TextField label="Original price (strikethrough)" value={s.offer_original_price} onChange={v => setS({ ...s, offer_original_price: v })} />
          </div>
          <JsonField label="Offer features (array of strings)" value={s.offer_features} onChange={v => setS({ ...s, offer_features: v })} />
          <TextField label="CTA label" value={s.offer_cta_label} onChange={v => setS({ ...s, offer_cta_label: v })} />
          <TextField label="CTA URL" value={s.offer_cta_url} onChange={v => setS({ ...s, offer_cta_url: v })} />
        </div>

        <div className="flex justify-end pt-2">
          <button type="submit" disabled={loading} data-testid="settings-save" className="btn-cyan rounded-full px-6 py-2.5 text-sm font-medium">{loading ? 'Saving…' : 'Save settings'}</button>
        </div>
      </form>
    </div>
  );
}
